import {
  holdMusicUrl,
  notificationSoundUrl,
  ringtoneUrl,
  RINGBACK_PATTERNS,
  type HoldMusic,
  type NotificationSound,
  type Ringback,
  type Ringtone,
} from '@avslack/shared';

/**
 * Hangok lejatszasa.
 *
 * A hangok FAJLOK (lasd packages/shared/src/sounds.ts), es a sajat
 * kiszolgalonkrol jonnek. A lejatszashoz `Audio` elemet hasznalunk, nem a Web
 * Audio API-t: itt nem szintetizalunk, csak lejatszunk, es az `Audio` ehhez
 * kevesebb kod es kevesebb hibalehetoseg.
 *
 * SOSEM dob hibat egyik fuggveny sem. Egy meg nem szolalo hang bosszanto, de
 * egy hibaval elszallo ertesites-kezelo azt jelentene, hogy maga az ertesites
 * sem jelenik meg.
 */

/**
 * Az elso lejatszas a bongeszo autoplay-vedelmebe utkozhet.
 *
 * Amig a felhasznalo nem kattintott az oldalon, a `play()` elutasitott
 * igerettel ter vissza. Ez nem hiba: elnyeljuk, es a kovetkezo hang - ami mar
 * interakcio utan jon - meg fog szolalni.
 */
function play(audio: HTMLAudioElement): void {
  void audio.play().catch(() => undefined);
}

// ─── Hangero ──────────────────────────────────────────────────────────────────

/**
 * A felhasznalo hangereje, SZORZOKENT.
 *
 * Eddig minden hang beegetett szinten szolt, es a felhasznalo kezeben csak a
 * "szoljon vagy ne" volt. Akinek hangos volt, annak csak a kikapcsolas maradt
 * - a rendszer hangereje ugyanis NEM szabalyozza ezeket: az ertesitest
 * szandekosan a sajat hangunkkal jatsszuk le (`silent: true` az ertesitesen),
 * tehat a Windows ertesitesi beallitasainak semmi koze hozza.
 *
 * Szorzo, nem abszolut ertek: a hangok egymashoz kepesti aranya megmarad - a
 * csengohang tovabbra is hangosabb, mint a varakozo zene.
 *
 * MODUL-SZINTU allapot, nem React: a hangokat olyan helyekrol is inditjuk,
 * ahol nincs komponens-fa (ertesites-kezelo, emlekezteto-busz). A `SoundVolume`
 * komponens (lasd `App`) tartja naprakeszen a beallitasbol.
 */
let multiplier = 1;

/** 0-100 szazalek. A hatarok kozé szoritjuk: rossz ertek ne nemitson le mindent. */
export function setSoundVolume(percent: number): void {
  const clamped = Math.min(100, Math.max(0, Math.round(percent)));
  multiplier = clamped / 100;

  // A mar SZOLO hangok is kovessek: aki hivas kozben huzza lejjebb a csuszkat,
  // az most akarja halkabban hallani, nem a kovetkezo hivasban.
  if (ringing) ringing.volume = level(RINGTONE_LEVEL);
  if (holdAudio && fade === null) holdAudio.volume = level(HOLD_LEVEL);
}

/** Egy alapszint a tenyleges hangerore valtva. */
function level(base: number): number {
  return Math.min(1, Math.max(0, base * multiplier));
}

/** Az egyes hangok alapszintje - egymashoz kepesti arany, nem abszolut ertek. */
const NOTIFICATION_LEVEL = 0.6;
const RINGTONE_LEVEL = 0.7;
const HOLD_LEVEL = 0.22;

// ─── Ertesito hang ────────────────────────────────────────────────────────────

/**
 * Fajlonkent EGY elemet tartunk.
 *
 * Igy a masodik ertesitesnel mar nincs betoltes, es nem szaporodnak a
 * hangelemek a memoriaban egy hosszu munkanap alatt.
 */
const cache = new Map<string, HTMLAudioElement>();

function element(url: string, volume: number): HTMLAudioElement {
  let audio = cache.get(url);
  if (!audio) {
    audio = new Audio(url);
    audio.preload = 'auto';
    cache.set(url, audio);
  }
  audio.volume = volume;
  return audio;
}

export function playNotificationSound(name: NotificationSound): void {
  const url = notificationSoundUrl(name);
  if (!url || multiplier === 0) return;

  try {
    const audio = element(url, level(NOTIFICATION_LEVEL));
    // Visszatekeres: gyors egymasutanban erkezo ertesiteseknel is szoljon
    // mindegyik, ne csak az elso.
    audio.currentTime = 0;
    play(audio);
  } catch {
    // A hang nem kritikus - a vizualis ertesites ettol fuggetlenul megjelenik.
  }
}

// ─── Csengohang ───────────────────────────────────────────────────────────────

let ringing: HTMLAudioElement | null = null;

/**
 * Hivas-jelzes: ismetlodik, amig valaki fel nem veszi.
 *
 * Egy felugro ablakot konnyu elnezni, ha eppen mas ablakban dolgozunk - a hang
 * az, ami tenyleg atszol.
 */
export function startRingtone(name: Ringtone): void {
  stopRingtone();

  const url = ringtoneUrl(name);
  if (!url) return;

  try {
    // A csengohang KULON elem, nem a gyorsitotarbol valo: kulonben a
    // `loop = true` ott ragadna a kesobbi lejatszasokon.
    const audio = new Audio(url);
    audio.loop = true;
    audio.volume = level(RINGTONE_LEVEL);
    ringing = audio;
    play(audio);
  } catch {
    ringing = null;
  }
}

export function stopRingtone(): void {
  if (!ringing) return;
  const audio = ringing;
  ringing = null;
  try {
    audio.pause();
    audio.currentTime = 0;
  } catch {
    // mar leallt
  }
}

// ─── Varakozo zene ────────────────────────────────────────────────────────────

let holdAudio: HTMLAudioElement | null = null;
let fade: ReturnType<typeof setInterval> | null = null;

/** Lagy hangero-valtas. A hirtelen indulo vagy elvagott zene megijeszt. */
function rampTo(audio: HTMLAudioElement, target: number, seconds: number, done?: () => void): void {
  if (fade !== null) clearInterval(fade);

  const steps = Math.max(1, Math.round(seconds * 20));
  const from = audio.volume;
  let step = 0;

  fade = setInterval(() => {
    step += 1;
    const value = from + ((target - from) * step) / steps;
    audio.volume = Math.min(1, Math.max(0, value));
    if (step < steps) return;

    if (fade !== null) clearInterval(fade);
    fade = null;
    done?.();
  }, 50);
}

/**
 * Halk zene, amig egyedul vagyunk a huddle-ben.
 *
 * A hangero szandekosan alacsony: hattér, nem musor. Amint valaki belep,
 * elhalkul - onnantol a beszelgetes a lenyeg.
 */
export function startHoldMusic(name: HoldMusic): void {
  const url = holdMusicUrl(name);
  if (!url || holdAudio) return;

  try {
    const audio = new Audio(url);
    audio.loop = true;
    audio.volume = 0;
    holdAudio = audio;
    play(audio);
    rampTo(audio, level(HOLD_LEVEL), 3);
  } catch {
    holdAudio = null;
  }
}

export function stopHoldMusic(): void {
  if (!holdAudio) return;
  const audio = holdAudio;
  holdAudio = null;

  rampTo(audio, 0, 1, () => {
    try {
      audio.pause();
    } catch {
      // mar leallt
    }
  });
}

// ─── Visszacsengetes ──────────────────────────────────────────────────────────

/**
 * Amit MI hallunk, amig a masik felveszi.
 *
 * Ez az egyetlen hang, amit ELOALLITUNK, nem fajlbol jatszunk - es jo okkal:
 * egy visszacsengetes tiszta szinusz, meghatarozott utemmel. Pontosan ezt
 * csinalja a telefonkozpont is, tehat itt a szintezis nem kompromisszum,
 * hanem a hiteles megoldas.
 *
 * A hangeronek szandekosan halknak kell lennie: ez hattérjelzes arrol, hogy a
 * hivas tart, nem figyelemfelkeltes.
 */
let ringbackCtx: AudioContext | null = null;
let ringbackTimer: ReturnType<typeof setInterval> | null = null;

/** A csorges alapszintje. Halk: hattérjelzes, nem csengohang. */
const RINGBACK_LEVEL = 0.16;

export function startRingback(name: Ringback): void {
  stopRingback();

  // A "zene" es a "nincs" nem ide tartozik - azokat a hivo intezi el.
  if (name === 'zene' || name === 'nincs') return;
  const pattern = RINGBACK_PATTERNS[name];

  try {
    const ctx = new AudioContext();
    ringbackCtx = ctx;

    /*
      A bongeszo FELFUGGESZTVE inditja a hangkornyezetet, amig nem volt
      felhasznaloi mozdulat. A hivas inditasa mozdulat, tehat itt mar szabad -
      de a `resume()` nelkul a kornyezet felfuggesztve MARADNA, es a csorges
      nemaban jarna le. Csendes hiba, pont az a fajta, amit nem vennenk eszre.
    */
    void ctx.resume().catch(() => undefined);

    const burst = () => {
      const start = ctx.currentTime;
      const length = pattern.onMs / 1000;

      /*
        Lagy be- es kifutas.

        Egy hirtelen inditott vagy elvagott szinusz KATTAN - a hangszoro
        membranja ugrik egyet. A 20 ezredmasodperces fel- es lefutas ezt
        tunteti el; roviden ennyi a kulonbseg egy telefon es egy hibauzenet
        hangja kozott.
      */
      const peak = level(RINGBACK_LEVEL);
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(peak, start + 0.02);
      gain.gain.setValueAtTime(peak, start + length - 0.02);
      gain.gain.linearRampToValueAtTime(0, start + length);

      for (const frequency of pattern.frequencies) {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = frequency;
        osc.connect(gain);
        osc.start(start);
        osc.stop(start + length + 0.05);
      }
    };

    burst();
    ringbackTimer = setInterval(burst, pattern.onMs + pattern.offMs);
  } catch {
    // A hang nem kritikus: a hivas ettol fuggetlenul tart.
    stopRingback();
  }
}

export function stopRingback(): void {
  if (ringbackTimer !== null) {
    clearInterval(ringbackTimer);
    ringbackTimer = null;
  }
  if (!ringbackCtx) return;

  const ctx = ringbackCtx;
  ringbackCtx = null;
  try {
    void ctx.close();
  } catch {
    // mar bezarult
  }
}

// ─── Kezemeles ────────────────────────────────────────────────────────────────

/**
 * Apro "pitty", amikor valaki jelentkezik az olelesben.
 *
 * Ez is ELOALLITOTT hang, ugyanazert, amiert a visszacsengetes: ket rovid
 * szinusz-hang nem er egy kulon fajlt. Szandekosan nagyon halk es nagyon
 * rovid - a beszelgetest nem szakithatja felbe, csak annyit mond: "valaki
 * szolni szeretne". A tobbit a csempen levo kez mutatja.
 *
 * Egyetlen hangkornyezetet tartunk: egy hosszu megbeszelesen sok jelentkezes
 * lehet, es a bongeszo az egyszerre nyitott `AudioContext`-ek szamat korlatozza.
 */
let chimeCtx: AudioContext | null = null;

/** A pitty alapszintje. A visszacsengetesnel is halkabb. */
const HAND_CHIME_LEVEL = 0.08;

export function playHandRaiseChime(): void {
  if (multiplier === 0) return;

  try {
    chimeCtx ??= new AudioContext();
    const ctx = chimeCtx;
    void ctx.resume().catch(() => undefined);

    const peak = level(HAND_CHIME_LEVEL);
    // Felfele lepo ket hang: kerdes-szeru, nem riaszto.
    [880, 1320].forEach((frequency, index) => {
      const start = ctx.currentTime + index * 0.09;
      const length = 0.08;

      // Lagy fel- es lefutas - kattanas nelkul (lasd a visszacsengetest).
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(peak, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + length);

      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = frequency;
      osc.connect(gain);
      osc.start(start);
      osc.stop(start + length + 0.02);
    });
  } catch {
    // A hang nem kritikus: a kez a csempen ettol fuggetlenul latszik.
  }
}
